import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { createLeaveType, listLeaveTypes, updateLeaveType } from '@/api/services/leaveTypes.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import type { CreateLeaveTypeDto, UpdateLeaveTypeDto } from '@/features/leave/types/leave.types'

export const leaveTypeKeys = {
  all: ['leave-types'] as const,
  list: (includeInactive: boolean) => ['leave-types', { includeInactive }] as const,
}

export function useLeaveTypes(options?: { includeInactive?: boolean; enabled?: boolean }) {
  const includeInactive = options?.includeInactive ?? false
  return useQuery({
    queryKey: leaveTypeKeys.list(includeInactive),
    queryFn: () => listLeaveTypes(includeInactive),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: options?.enabled !== false,
  })
}

export function useCreateLeaveType() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateLeaveTypeDto) => createLeaveType(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leaveTypeKeys.all })
      toast.success('Leave type created.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useUpdateLeaveType() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ leaveTypeId, body }: { leaveTypeId: string; body: UpdateLeaveTypeDto }) =>
      updateLeaveType(leaveTypeId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leaveTypeKeys.all })
      toast.success('Leave type updated.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}
